import { useFormContext } from 'react-hook-form';
import FileBase from '../../core/form/FileBase';
import FileIndicator from '../../core/FileIndicator';
import Tooltip from '../../core/Tooltip';
import PropTypes from 'prop-types';

const ImportAspenForm = ({ isSubmitting, errorMessage }) => {
  const { setValue, register, watch } = useFormContext();
  const selectedFiles = watch('files') || [];

  const handleFileChange = async files => {
    if (!files?.length) {
      return;
    }

    setValue('files', [...selectedFiles, ...Array.from(files)]);
  };

  const handleRemove = index => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setValue('files', updatedFiles);
  };

  return (
    <div className="space-y-6">
      <h3 className="font-bold text-lg text-center mb-4">Upload a Model</h3>
      <p className="mb-6">
        Sesame allows users to easily transform Aspen Plus® and Aspen HYSYS® simulations into Sesame modules.
        Uploading an Aspen Plus&reg; bkp file or Aspen HYSYS hsc file will auto-populate Sesame&apos;s Ratio Module
        builder.
      </p>
      <ol className="list-decimal list-inside mb-6">
        <li className="mb-3">
          Save your <b>converged</b> Aspen Plus file as a .bkp file or Aspen HYSYS file as a .hsc.
        </li>
        <li className="mb-3">Upload the .bkp file in the import box below.</li>
        <li className="mb-3">
          Sesame will parse this file and auto-populate the Ratio Module builder with stream data it obtained.
        </li>
        <li className="mb-3">
          After validating the inlet and outlet streams, input TEA data to use it as a custom module.
        </li>
      </ol>
      <p className="text-center font-bold">
        Sesame highly recommends that you verify the streams in the Mass and Energy Balance. Particularly complex flowsheets may have small errors.
      </p>

      <div className="flex flex-col gap-2">
        {selectedFiles && selectedFiles.length > 0 && (
          <>
            {selectedFiles.map((file, index) => (
              <FileIndicator key={index} file={file} onRemove={() => handleRemove(index)} />
            ))}
          </>
        )}
        <FileBase
          name="files"
          className="text-md"
          onUpload={handleFileChange}
          acceptedFormats=".bkp,.hsc,.acm,.acmf"
          label="custom models"
          isUploading={isSubmitting}
          multiple={true}
        />
      </div>

      <div className="flex items-center gap-2">
        <input type="checkbox" id="parameterize" className="checkbox checkbox-sm" {...register('parameterize')} />
        <label htmlFor="parameterize" className="text-sm font-medium">
          Enable full parametrization
        </label>
        <Tooltip
          label="Enable full parametrization"
          text="If turned on, users will be allowed to adjust block parameters. Sesame will dynamically gather the balance results based on the specified parameter values. When this mode is active, the initial file upload may take a few hours to complete."
        />
      </div>

      <details className="collapse collapse-arrow border border-gray-200 rounded-lg">
        <summary className="collapse-title font-medium">
          Video: How to create a Custom Module from an Aspen Simulation
        </summary>
        <div className="collapse-content">
          <video controls className="w-full max-w-4xl mx-auto rounded-lg shadow-lg" preload="metadata">
            <source src="https://docs.sesame.software/aspen-plus-to-sesame-tutorial.mp4" type="video/mp4" />
            Your browser does not support the video tag. Please download the video file to view it.
          </video>
        </div>
      </details>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="font-medium text-red-700 text-sm mb-2">
            File is not convertible into a Sesame module for the following reason:
          </p>
          <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
            {errorMessage?.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
          <p className="text-red-700 text-sm mt-2">Please correct this issue and re-upload the file.</p>
        </div>
      )}
    </div>
  );
};

ImportAspenForm.propTypes = {
  isSubmitting: PropTypes.bool.isRequired,
  errorMessage: PropTypes.arrayOf(PropTypes.string),
};

export default ImportAspenForm;
